import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  Home,
  X,
  Crown,
  User,
  LogOut,
  ChevronDown,
  Bell,
  Trophy,
  Gift,
  Ticket,
  Settings,
  Gamepad2,
  Plus,
  CircleAlert,
  Users,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { AuthModal } from "@/components/auth/AuthModal";
import useAdminRealtime from "@/hooks/useAdminRealtime";
import { ADMIN_UNREAD_EVENT, emitAdminUnreadSummary } from "@/lib/adminUnreadEvents";
import { USER_NOTIFICATIONS_EVENT, emitUserNotificationState } from "@/lib/userNotificationEvents";
import { hasUserRole, isAdminUser } from "@/lib/userAccess";
import UserAvatar from "@/components/UserAvatar";

const API = process.env.REACT_APP_BACKEND_URL;
const VERTICAL_LOGO = "/assets/logo-vertical.png";

const DEFAULT_NAV_LINKS = [
  { path: "/", label: "InÃƒÂ­cio" },
  { path: "/eventos", label: "Eventos" },
  { path: "/contratar", label: "Contratar" },
  { path: "/jogos", label: "Jogos" },
  { path: "/clube", label: "Quiz Club", highlight: true },
  { path: "/placar", label: "Placar" },
  { path: "/temas", label: "Alertas" },
  { path: "/quizmaker", label: "Quiz Maker" },
  { path: "/sobre", label: "Sobre" },
];

const APP_PROFILE_LINKS = [
  { path: "/perfil", label: "Meu Perfil", icon: User },
  { path: "/notificacoes", label: "Notificações", icon: Bell },
  { path: "/meus-ingressos", label: "Minha Carteira", icon: Ticket },
  { path: "/meu-plano", label: "Meu Plano", icon: Crown },
  { path: "/minha-equipe", label: "Minha Equipe", icon: Users },
  { path: "/meus-quizzes", label: "Meus Quizzes", icon: Gamepad2 },
  { path: "/perfil#conquistas", label: "Conquistas", icon: Gift },
  { path: "/placar", label: "Placar", icon: Trophy },
  { path: "/configuracao", label: "Configuração", icon: Settings },
];

const PROFILE_LINKS = [
  { path: "/perfil", label: "Meu Perfil", icon: User },
  { path: "/notificacoes", label: "Notificações", icon: Bell },
  { path: "/meus-ingressos", label: "Minha Carteira", icon: Ticket },
  { path: "/meu-plano", label: "Meu Plano", icon: Crown },
  { path: "/minha-equipe", label: "Minha Equipe", icon: Users },
  { path: "/beneficios", label: "BenefÃƒÂ­cios", icon: Gift },
  { path: "/placar", label: "Placar", icon: Trophy },
];

function fixMojibake(value) {
  if (typeof value !== "string") return value;
  if (!/[ÃƒÆ’Ãƒâ€šÃƒÂ¢]/.test(value)) return value;

  try {
    return decodeURIComponent(escape(value));
  } catch {
    return value.replace(/Ãƒâ€š/g, "");
  }
}

function formatNotificationDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function withOnDemandAdminChild(links, isStrictAdmin) {
  return links.map((link) => {
    if (link.path !== "/contratar") return link;

    const nextChildren = (link.children || []).filter((child) => child.path !== "/contratar/on-demand");
    if (isStrictAdmin) {
      nextChildren.push({ path: "/contratar/on-demand", label: "Criador de quizzes" });
    }

    return {
      ...link,
      children: nextChildren,
    };
  });
}

function withCashbackChild(links, canAccessCashback) {
  return links.map((link) => {
    if (link.path !== "/clube") return link;

    const nextChildren = (link.children || []).filter((child) => child.path !== "/cashback");
    if (canAccessCashback) {
      nextChildren.push({ path: "/cashback", label: "Cashback" });
    }
    return { ...link, children: nextChildren };
  });
}

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showInboxMenu, setShowInboxMenu] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [navLinks, setNavLinks] = useState(DEFAULT_NAV_LINKS);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [adminUnreadTotal, setAdminUnreadTotal] = useState(0);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { user, loading: authLoading, logout } = useAuth();
  const isLight = resolvedTheme === "light";
  const isGamesPage = location.pathname.startsWith("/jogos");
  const isStrictAdmin = isAdminUser(user);
  const canAccessAdminPanel = hasUserRole(user, "analyst");
  const canAccessPromoterPanel = hasUserRole(user, "promoter");
  const canAccessPresenterPanel = hasUserRole(user, "presenter");
  const canAccessCashback = isStrictAdmin || ["pro", "master"].includes(String(user?.subscription_plan || "").toLowerCase())
    && !["inactive", "cancelled", "canceled", "expired"].includes(String(user?.subscription_status || "").toLowerCase());

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setShowUserMenu(false);
    setShowInboxMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [isOpen]);

  useEffect(() => {
    if (!showUserMenu && !showInboxMenu) return;
    const handleClick = () => setShowUserMenu(false);
    const handleInboxClick = () => setShowInboxMenu(false);
    document.addEventListener("click", handleClick);
    document.addEventListener("click", handleInboxClick);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("click", handleInboxClick);
    };
  }, [showUserMenu, showInboxMenu]);

  useEffect(() => {
    const fetchNavigation = async () => {
      try {
        const res = await fetch(`${API}/api/cms/settings`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.navigation?.items?.length) return;

        let cmsLinks = data.navigation.items.map((item) => {
          const shouldMapToQuizMaker = item.path === "/perguntas" || item.path === "/meus-quizzes";
          const normalizedPath = shouldMapToQuizMaker ? "/quizmaker" : item.path;
          const normalizedLabel = shouldMapToQuizMaker ? "Quiz Maker" : fixMojibake(item.label);
          return {
            path: normalizedPath,
            label: normalizedLabel,
            highlight: item.highlight || item.label.toLowerCase().includes("club"),
            children: item.children || [],
          };
        });

        if (!cmsLinks.some((link) => link.path === "/clube")) {
          cmsLinks.splice(4, 0, { path: "/clube", label: "Quiz Club", highlight: true });
        } else {
          cmsLinks = cmsLinks.map((link) => (
            link.path === "/clube" ? { ...link, highlight: true } : link
          ));
        }

        if (!cmsLinks.some((link) => link.path === "/placar")) {
          cmsLinks.push({ path: "/placar", label: "Placar" });
        }

        cmsLinks = cmsLinks.filter((link, index, array) => (
          array.findIndex((candidate) => candidate.path === link.path) === index
        ));

        setNavLinks(cmsLinks);
      } catch (error) {
        console.error("Error loading navigation:", error);
      }
    };

    fetchNavigation();
  }, []);

  useEffect(() => {
    if (!user || !canAccessAdminPanel) {
      setAdminUnreadTotal(0);
      return undefined;
    }

    let isMounted = true;

    const loadAdminUnreadSummary = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const response = await fetch(`${API}/api/admin/unread-summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) return;

        const data = await response.json();
        if (isMounted) {
          setAdminUnreadTotal(data?.total || 0);
          emitAdminUnreadSummary(data);
        }
      } catch (error) {
        console.error("Error loading admin unread summary for navbar:", error);
      }
    };

    loadAdminUnreadSummary();
    const intervalId = window.setInterval(loadAdminUnreadSummary, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [user, canAccessAdminPanel]);

  useEffect(() => {
    if (!user || !canAccessAdminPanel) return undefined;

    const handleUnreadSummaryUpdate = (event) => {
      setAdminUnreadTotal(event.detail?.total || 0);
    };

    window.addEventListener(ADMIN_UNREAD_EVENT, handleUnreadSummaryUpdate);
    return () => {
      window.removeEventListener(ADMIN_UNREAD_EVENT, handleUnreadSummaryUpdate);
    };
  }, [user, canAccessAdminPanel]);

  useEffect(() => {
    if (!user) {
      setInboxUnreadCount(0);
      setRecentNotifications([]);
      return undefined;
    }

    let isMounted = true;

    const loadInboxState = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const [countResponse, inboxResponse] = await Promise.all([
          fetch(`${API}/api/notifications/inbox/unread-count`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/notifications/inbox?limit=5`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!countResponse.ok || !inboxResponse.ok) return;

        const countData = await countResponse.json();
        const inboxData = await inboxResponse.json();
        if (!isMounted) return;

        setInboxUnreadCount(countData?.unread_count || 0);
        setRecentNotifications(inboxData?.items || []);
        emitUserNotificationState({
          unreadCount: countData?.unread_count || 0,
          recentItems: inboxData?.items || [],
        });
      } catch (error) {
        console.error("Error loading inbox state:", error);
      }
    };

    loadInboxState();
    const intervalId = window.setInterval(loadInboxState, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;

    const handleUserNotificationsUpdate = (event) => {
      const detail = event.detail || {};
      setInboxUnreadCount(detail.unreadCount || 0);
      if (Array.isArray(detail.recentItems)) {
        setRecentNotifications(detail.recentItems);
      }
    };

    window.addEventListener(USER_NOTIFICATIONS_EVENT, handleUserNotificationsUpdate);
    return () => {
      window.removeEventListener(USER_NOTIFICATIONS_EVENT, handleUserNotificationsUpdate);
    };
  }, [user]);

  useAdminRealtime({
    enabled: Boolean(user && canAccessAdminPanel),
    onAdminUpdate: async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const response = await fetch(`${API}/api/admin/unread-summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) return;

        const data = await response.json();
        setAdminUnreadTotal(data?.total || 0);
        emitAdminUnreadSummary(data);
      } catch (error) {
        console.error("Error refreshing navbar admin unread summary:", error);
      }
    },
    onUnreadStateUpdate: (payload) => {
      if (payload?.user_id !== user?.id) return;
      const summary = payload?.summary || {};
      setAdminUnreadTotal(summary?.total || 0);
      emitAdminUnreadSummary(summary);
    },
  });

  const markInboxNotificationRead = async (notificationId) => {
    const token = localStorage.getItem("auth_token");
    if (!token || !notificationId) return;

    try {
      const response = await fetch(`${API}/api/notifications/inbox/${notificationId}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      const nextItems = recentNotifications.map((item) => (
        item.id === notificationId && !item.read_at
          ? { ...item, read_at: new Date().toISOString() }
          : item
      ));
      setRecentNotifications(nextItems);
      setInboxUnreadCount(data?.unread_count || 0);
      emitUserNotificationState({
        unreadCount: data?.unread_count || 0,
        recentItems: nextItems,
      });
    } catch (error) {
      console.error("Error marking navbar inbox notification as read:", error);
    }
  };

  const desktopLinks = useMemo(
    () => withCashbackChild(withOnDemandAdminChild(navLinks, isStrictAdmin), canAccessCashback),
    [navLinks, isStrictAdmin, canAccessCashback],
  );
  const quizClubColor = isLight ? "var(--lotq-light-gold-strong)" : "#fccf1f";
  const quizClubBorder = isLight ? "rgba(148, 98, 0, 0.28)" : "rgba(252, 207, 31, 0.3)";
  const navShellClass = isLight
    ? "bg-white/95 border-b border-black/5 shadow-[0_8px_30px_rgba(23,23,23,0.06)] backdrop-blur-md"
    : "bg-zinc-950 border-b border-white/8 shadow-[0_12px_30px_rgba(0,0,0,0.35)]";
  const navButtonClass = isLight
    ? "rounded-2xl border border-black/10 bg-white text-[#221844]"
    : "rounded-2xl border border-white/10 bg-zinc-900 text-white";
  const dropdownClass = isLight
    ? "absolute left-0 top-full z-50 mt-0 w-48 pt-2"
    : "absolute left-0 top-full z-50 mt-0 w-48 pt-2";
  const dropdownPanelClass = isLight
    ? "rounded-xl border border-black/10 bg-white py-2 shadow-[0_18px_50px_rgba(23,23,23,0.12)]"
    : "rounded-xl border border-white/10 bg-zinc-950 py-2 shadow-xl";
  const userMenuClass = isLight
    ? "absolute right-0 z-50 mt-2 w-56 rounded-xl border border-black/10 bg-white py-2 shadow-[0_18px_50px_rgba(23,23,23,0.12)]"
    : "absolute right-0 z-50 mt-2 w-56 rounded-xl border border-white/10 bg-zinc-950 py-2 shadow-xl";
  const userMenuButtonClass = isLight
    ? "flex items-center gap-2 rounded-full border border-black/10 bg-white py-1 pl-1 pr-3 transition-colors hover:border-[#b77900]/30"
    : "flex items-center gap-2 rounded-full border border-white/10 bg-zinc-900 py-1 pl-1 pr-3 transition-colors hover:border-[#fccf1f]/30";
  const adminUnreadBadge = adminUnreadTotal > 99 ? "99+" : adminUnreadTotal;
  const inboxUnreadBadge = inboxUnreadCount > 99 ? "99+" : inboxUnreadCount;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navShellClass}`}
        data-testid="main-navbar"
      >
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="relative flex h-16 items-center justify-between lg:h-20">
            <button
              onClick={() => setIsOpen((prev) => !prev)}
              className={`${navButtonClass} p-2 lg:hidden`}
              data-testid="mobile-menu-toggle"
              type="button"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center lg:translate-x-0"
                data-testid="logo-link"
              >
                <img
                  src={VERTICAL_LOGO}
                  alt="Lord of the Quiz"
                  className={`w-auto object-contain md:h-16 ${isGamesPage ? "h-8" : "h-9"}`}
                  style={isLight ? { filter: "brightness(0) saturate(100%)" } : undefined}
                />
              </Link>
            </div>

            <div className="hidden items-center gap-6 lg:flex">
              {desktopLinks.map((link) => (
                link.children?.length > 0 ? (
                  <div
                    key={link.path}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.path)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      className={`flex items-center gap-1 font-outfit text-sm font-medium uppercase tracking-wider transition-colors hover:text-[#fccf1f] ${
                        location.pathname === link.path || link.children.some((c) => location.pathname === c.path)
                          ? "text-[#fccf1f]"
                          : "text-white"
                      }`}
                      style={link.highlight ? { color: quizClubColor } : undefined}
                      type="button"
                    >
                      {link.highlight && <Crown className="h-3.5 w-3.5" />}
                      {fixMojibake(link.label)}
                      <ChevronDown className="h-3 w-3" />
                    </button>

                    {openDropdown === link.path && (
                      <div className={dropdownClass}>
                        <div className={dropdownPanelClass}>
                          <Link
                            to={link.path}
                            className={`block px-4 py-2 text-sm transition-colors hover:bg-white/5 hover:text-[#fccf1f] ${isLight ? "text-zinc-700" : "text-zinc-300"}`}
                          >
                            {link.path === "/clube" ? "Meus benefícios" : `${fixMojibake(link.label)} (Principal)`}
                          </Link>
                          <div className={`mt-1 pt-1 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}>
                            {link.children.map((child, idx) => (
                              <Link
                                key={idx}
                                to={child.path}
                                className={`block px-4 py-2 text-sm transition-colors hover:bg-white/5 hover:text-[#fccf1f] ${
                                  location.pathname === child.path ? "text-[#fccf1f]" : isLight ? "text-zinc-700" : "text-zinc-300"
                                }`}
                              >
                                {fixMojibake(child.label)}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-outfit text-sm font-medium uppercase tracking-wider transition-colors hover:text-[#fccf1f] ${
                      link.highlight && location.pathname !== link.path
                        ? "flex items-center gap-1 text-[#fccf1f]/80"
                        : location.pathname === link.path
                        ? "text-[#fccf1f]"
                        : "text-white"
                    }`}
                    style={link.highlight ? { color: quizClubColor } : undefined}
                  >
                    {link.highlight && <Crown className="h-3.5 w-3.5" />}
                    {fixMojibake(link.label)}
                  </Link>
                )
              ))}

              {!authLoading && (
                user ? (
                  <div className="ml-2 flex items-center gap-2">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setShowInboxMenu((prev) => !prev);
                          setShowUserMenu(false);
                        }}
                        className={`relative flex h-10 w-10 items-center justify-center ${navButtonClass}`}
                        data-testid="desktop-inbox-btn"
                        type="button"
                      >
                        <Bell className="h-4 w-4" />
                        {inboxUnreadCount > 0 ? (
                          <span className="absolute -right-1 -top-1 inline-flex min-w-[1.15rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                            {inboxUnreadBadge}
                          </span>
                        ) : null}
                      </button>

                      {showInboxMenu && (
                        <div className={userMenuClass.replace("w-56", "w-80")}>
                          <div className={`flex items-center justify-between px-4 py-3 ${isLight ? "border-b border-black/5" : "border-b border-white/5"}`}>
                            <div>
                              <p className={`font-outfit text-sm font-semibold ${isLight ? "text-zinc-900" : "text-white"}`}>Inbox</p>
                              <p className="font-outfit text-xs text-zinc-500">
                                {inboxUnreadCount > 0 ? `${inboxUnreadCount} nao lida(s)` : "Tudo em dia"}
                              </p>
                            </div>
                            <Link
                              to="/notificacoes"
                              onClick={() => setShowInboxMenu(false)}
                              className="text-xs font-semibold uppercase tracking-[0.18em] text-[#fccf1f]"
                            >
                              Ver todas
                            </Link>
                          </div>
                          <div className="max-h-[22rem] overflow-y-auto">
                            {recentNotifications.length ? recentNotifications.map((item) => (
                              <Link
                                key={item.id}
                                to="/notificacoes"
                                onClick={() => {
                                  if (!item.read_at) markInboxNotificationRead(item.id);
                                  setShowInboxMenu(false);
                                }}
                                className={`block px-4 py-3 transition-colors hover:bg-white/5 ${
                                  item.read_at ? (isLight ? "text-zinc-700" : "text-zinc-300") : "bg-[#fccf1f]/8 text-white"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="truncate font-outfit text-sm font-semibold">{item.title}</p>
                                    <p className={`mt-1 line-clamp-2 text-xs ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>{item.message}</p>
                                  </div>
                                  {!item.read_at ? <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#fccf1f]" /> : null}
                                </div>
                                <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">{formatNotificationDate(item.created_at)}</p>
                              </Link>
                            )) : (
                              <div className="px-4 py-6 text-center text-sm text-zinc-500">
                                Nenhuma notificacao por enquanto.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setShowUserMenu((prev) => !prev);
                          setShowInboxMenu(false);
                        }}
                        className={userMenuButtonClass}
                        data-testid="user-menu-btn"
                        type="button"
                      >
                        <UserAvatar
                          picture={user.picture}
                          className="h-7 w-7 rounded-full object-cover"
                          fallbackClassName={`h-7 w-7 rounded-full ${isLight ? "bg-[#6e46d6]/12" : "bg-[#fccf1f]/20"}`}
                          iconClassName="h-3.5 w-3.5"
                        />
                        <span className={`max-w-[80px] truncate font-outfit text-xs ${isLight ? "text-zinc-700" : "text-zinc-300"}`}>
                          {user.display_name?.split(" ")[0] || user.name?.split(" ")[0]}
                        </span>
                        <ChevronDown className={`h-3 w-3 ${isLight ? "text-zinc-400" : "text-zinc-500"}`} />
                        {canAccessAdminPanel && adminUnreadTotal > 0 ? (
                          <span className="ml-1 inline-flex min-w-[1.15rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                            {adminUnreadBadge}
                          </span>
                        ) : null}
                      </button>

                      {showUserMenu && (
                        <div className={userMenuClass.replace("w-56", "w-48")}>
                        <div className={`px-4 py-2 ${isLight ? "border-b border-black/5" : "border-b border-white/5"}`}>
                          <p className={`truncate font-outfit text-sm ${isLight ? "text-zinc-900" : "text-white"}`}>{user.display_name || user.name}</p>
                          <p className={`truncate font-outfit text-xs ${isLight ? "text-zinc-500" : "text-zinc-500"}`}>{user.email}</p>
                        </div>
                        {APP_PROFILE_LINKS.map((link) => (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setShowUserMenu(false)}
                            className={`flex items-center gap-2 px-4 py-2.5 font-outfit text-sm transition-colors hover:bg-white/5 hover:text-[#fccf1f] ${isLight ? "text-zinc-700" : "text-zinc-300"}`}
                          >
                            <link.icon className="h-4 w-4" /> {fixMojibake(link.label)}
                          </Link>
                        ))}
                        {canAccessAdminPanel && (
                          <Link
                            to="/admin"
                            onClick={() => setShowUserMenu(false)}
                            className={`flex items-center justify-between gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}
                          >
                            <span className="flex items-center gap-2">
                              <Settings className="h-4 w-4" /> Painel do Admin
                            </span>
                            {adminUnreadTotal > 0 ? (
                              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                                {adminUnreadBadge}
                              </span>
                            ) : null}
                          </Link>
                        )}
                        {hasUserRole(user, "establishment") && <Link to="/estabelecimento" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10"><Home className="h-4 w-4" aria-hidden="true" /> Painel do Estabelecimento</Link>}
                        {canAccessPromoterPanel && (
                          <Link
                            to="/promotor"
                            onClick={() => setShowUserMenu(false)}
                            className={`flex items-center gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}
                          >
                            <Ticket className="h-4 w-4" /> Painel do Promotor
                          </Link>
                        )}
                        {canAccessPresenterPanel && (
                          <Link to="/apresentador" onClick={() => setShowUserMenu(false)} className={`flex items-center gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}>
                            <Trophy className="h-4 w-4" /> Painel do Apresentador
                          </Link>
                        )}
                        <button
                          onClick={() => { logout(); setShowUserMenu(false); }}
                          className={`flex w-full items-center gap-2 px-4 py-2.5 font-outfit text-sm transition-colors hover:bg-white/5 hover:text-red-400 ${isLight ? "text-zinc-500" : "text-zinc-400"}`}
                          type="button"
                        >
                          <LogOut className="h-4 w-4" /> Sair
                        </button>
                      </div>
                    )}
                  </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAuth(true)}
                    className="rounded-2xl bg-[#fccf1f] px-5 py-2.5 font-nunito text-sm font-bold uppercase tracking-wide text-black transition-colors hover:bg-[#e6bc1c]"
                    data-testid="desktop-login-btn"
                    type="button"
                  >
                    Entrar
                  </button>
                )
              )}
            </div>

            {!authLoading && user && (
              <div className="order-last flex items-center gap-2 lg:hidden">
                <Link
                  to="/quizmaker"
                  className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-[#fccf1f]"
                  data-testid="mobile-quizmaker-shortcut"
                  aria-label="Criar novo quiz"
                >
                  <Plus className="h-5 w-5" />
                </Link>
                <Link
                  to="/temas"
                  className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-[#fccf1f]"
                  data-testid="mobile-alerts-shortcut"
                  aria-label="Alertas"
                >
                  <CircleAlert className="h-5 w-5" />
                </Link>
                <Link
                  to="/placar"
                  className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-[#fccf1f]"
                  data-testid="mobile-score-shortcut"
                >
                  <Trophy className="h-4 w-4" />
                </Link>
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      setShowInboxMenu((prev) => !prev);
                      setShowUserMenu(false);
                    }}
                    className={`relative flex h-10 w-10 items-center justify-center ${navButtonClass}`}
                    data-testid="mobile-inbox-shortcut"
                    type="button"
                  >
                    <Bell className="h-4 w-4" />
                    {inboxUnreadCount > 0 ? (
                      <span className="absolute -right-1 -top-1 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                        {inboxUnreadBadge}
                      </span>
                    ) : null}
                  </button>

                  {showInboxMenu && (
                    <div className={userMenuClass.replace("w-56", "w-72")}>
                      <div className={`flex items-center justify-between px-4 py-3 ${isLight ? "border-b border-black/5" : "border-b border-white/5"}`}>
                        <div>
                          <p className={`font-outfit text-sm font-semibold ${isLight ? "text-zinc-900" : "text-white"}`}>Inbox</p>
                          <p className="font-outfit text-xs text-zinc-500">
                            {inboxUnreadCount > 0 ? `${inboxUnreadCount} nao lida(s)` : "Tudo em dia"}
                          </p>
                        </div>
                        <Link
                          to="/notificacoes"
                          onClick={() => setShowInboxMenu(false)}
                          className="text-xs font-semibold uppercase tracking-[0.18em] text-[#fccf1f]"
                        >
                          Abrir
                        </Link>
                      </div>
                      <div className="max-h-[22rem] overflow-y-auto">
                        {recentNotifications.length ? recentNotifications.map((item) => (
                          <Link
                            key={item.id}
                            to={item.cta_url || "/notificacoes"}
                            onClick={() => {
                              if (!item.read_at) markInboxNotificationRead(item.id);
                              setShowInboxMenu(false);
                            }}
                            className={`block px-4 py-3 transition-colors hover:bg-white/5 ${
                              item.read_at ? (isLight ? "text-zinc-700" : "text-zinc-300") : "bg-[#fccf1f]/8 text-white"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate font-outfit text-sm font-semibold">{item.title}</p>
                                <p className={`mt-1 line-clamp-2 text-xs ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>{item.message}</p>
                              </div>
                              {!item.read_at ? <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#fccf1f]" /> : null}
                            </div>
                            <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">{formatNotificationDate(item.created_at)}</p>
                          </Link>
                        )) : (
                          <div className="px-4 py-6 text-center text-sm text-zinc-500">
                            Nenhuma notificacao por enquanto.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      setShowUserMenu((prev) => !prev);
                      setShowInboxMenu(false);
                    }}
                    className={`relative flex h-10 w-10 items-center justify-center ${navButtonClass}`}
                    data-testid="mobile-user-shortcut"
                    type="button"
                  >
                    <UserAvatar
                      picture={user.picture}
                      className="h-10 w-10 rounded-2xl object-cover"
                      fallbackClassName="h-10 w-10 rounded-2xl"
                      iconClassName="h-4 w-4"
                    />
                    {canAccessAdminPanel && adminUnreadTotal > 0 ? (
                      <span className="absolute -right-1 -top-1 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                        {adminUnreadBadge}
                      </span>
                    ) : null}
                  </button>

                  {showUserMenu && (
                    <div className={userMenuClass}>
                      <div className={`px-4 py-2 ${isLight ? "border-b border-black/5" : "border-b border-white/5"}`}>
                        <p className={`truncate font-outfit text-sm ${isLight ? "text-zinc-900" : "text-white"}`}>{user.display_name || user.name}</p>
                        <p className="truncate font-outfit text-xs text-zinc-500">{user.email}</p>
                      </div>
                      {APP_PROFILE_LINKS.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          onClick={() => setShowUserMenu(false)}
                          className={`flex items-center gap-2 px-4 py-2.5 font-outfit text-sm transition-colors hover:bg-white/5 hover:text-[#fccf1f] ${isLight ? "text-zinc-700" : "text-zinc-300"}`}
                        >
                          <link.icon className="h-4 w-4" /> {fixMojibake(link.label)}
                        </Link>
                      ))}
                      {canAccessAdminPanel && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className={`flex items-center justify-between gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}
                        >
                          <span className="flex items-center gap-2">
                            <Settings className="h-4 w-4" /> Painel do Admin
                          </span>
                          {adminUnreadTotal > 0 ? (
                            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                              {adminUnreadBadge}
                            </span>
                          ) : null}
                        </Link>
                      )}
                      {hasUserRole(user, "establishment") && <Link to="/estabelecimento" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10"><Home className="h-4 w-4" aria-hidden="true" /> Painel do Estabelecimento</Link>}
                        {canAccessPromoterPanel && (
                        <Link
                          to="/promotor"
                          onClick={() => setShowUserMenu(false)}
                          className={`flex items-center gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}
                        >
                          <Ticket className="h-4 w-4" /> Painel do Promotor
                        </Link>
                      )}
                      {canAccessPresenterPanel && (
                        <Link to="/apresentador" onClick={() => setShowUserMenu(false)} className={`flex items-center gap-2 px-4 py-2.5 font-outfit text-sm text-[#fccf1f] transition-colors hover:bg-[#fccf1f]/10 ${isLight ? "border-t border-black/5" : "border-t border-white/5"}`}>
                          <Trophy className="h-4 w-4" /> Painel do Apresentador
                        </Link>
                      )}
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        className={`flex w-full items-center gap-2 px-4 py-2.5 font-outfit text-sm transition-colors hover:bg-white/5 hover:text-red-400 ${isLight ? "text-zinc-500" : "text-zinc-400"}`}
                        type="button"
                      >
                        <LogOut className="h-4 w-4" /> Sair
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {isOpen && (
        <div className={`fixed inset-0 z-[999] lg:hidden ${isLight ? "bg-white" : "bg-zinc-950"}`} data-testid="mobile-menu">
          <div className={`flex h-16 items-center justify-between px-4 ${isLight ? "border-b border-black/5 bg-white" : "border-b border-white/10 bg-zinc-950"}`}>
            <div>
              <p className={`font-nunito text-base font-bold ${isLight ? "text-zinc-900" : "text-white"}`}>Menu</p>
              <p className="font-outfit text-xs uppercase tracking-[0.2em] text-zinc-500">Acessos rápidos</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className={`flex h-10 w-10 items-center justify-center ${navButtonClass}`}
              data-testid="mobile-menu-close-top"
              type="button"
            >
              <X size={22} />
            </button>
          </div>

          <div
            className="space-y-4 overflow-y-auto overscroll-contain px-4 py-4"
            style={{ height: "calc(100dvh - 64px)" }}
          >
            <div className="space-y-3">
              <p className="px-1 font-outfit text-xs uppercase tracking-[0.25em] text-zinc-500">Explorar</p>
              {desktopLinks.map((link) => (
                <div key={link.path} className="space-y-2">
                  <Link
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex min-h-[48px] items-center rounded-2xl border px-4 py-3 font-outfit text-sm font-medium leading-tight ${
                      location.pathname === link.path
                        ? "border-[#fccf1f]/30 bg-[#fccf1f]/8 text-[#fccf1f]"
                        : isLight
                          ? "border-black/10 bg-white text-zinc-800"
                          : "border-white/10 bg-zinc-900 text-white"
                    }`}
                    style={link.highlight ? { color: quizClubColor, borderColor: quizClubBorder } : undefined}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                  >
                    {fixMojibake(link.label)}
                  </Link>

                  {link.children?.length > 0 && (
                    <div className="ml-4 space-y-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setIsOpen(false)}
                          className={`flex min-h-[44px] items-center rounded-2xl border px-4 py-2.5 font-outfit text-sm font-medium leading-tight ${
                            location.pathname === child.path
                              ? "border-[#fccf1f]/30 bg-[#fccf1f]/8 text-[#fccf1f]"
                              : isLight
                                ? "border-black/10 bg-white text-zinc-700"
                                : "border-white/10 bg-zinc-900/80 text-zinc-100"
                          }`}
                          data-testid={`mobile-nav-link-${child.label.toLowerCase().replace(" ", "-")}`}
                        >
                          {fixMojibake(child.label)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!authLoading && !user && (
              <button
                onClick={() => { setIsOpen(false); setShowAuth(true); }}
                className="w-full rounded-2xl bg-[#fccf1f] px-6 py-3 font-nunito text-sm font-bold uppercase tracking-wide text-black"
                data-testid="mobile-login-btn"
                type="button"
              >
                Entrar
              </button>
            )}

          </div>
        </div>
      )}

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}
